import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { appendFormLink, defaultTemplate } from "@smart/bridge-templates-basic";
import { Accordion, Spinner } from "@smart/itops-sb-design-system-dom";

import { ConfirmationMessage } from "./confirmation-message";
import { CustomReminderMessageBanner } from "./custom-reminder-message-banner";
import { MessageSection } from "./message-section";
import { ReminderScheduleSettings } from "./reminder-schedule-settings";
import { ReminderScheduleInput, ReminderScheduleValue } from "../../types";

type MessageContainerProps = {
  setting: {
    matter?: string | null;
    lead?: string | null;
    reminder?: string | null;
    confirmation?: string | null;
    redirectUrl?: string | null;
  };
  update: (values: {
    matter?: string;
    lead?: string;
    reminder?: string;
    confirmation?: string;
    redirectUrl?: string | null;
  }) => Promise<void>;
  reminderSchedule?: ReminderScheduleValue | null;
  updateReminderSchedule: (values: ReminderScheduleInput) => Promise<void>;
  loadScheduledReminderCount?: () => Promise<number>;
  customReminderMessageFormCount?: number;
  loading?: boolean;
};

export const MessageContainer = ({
  setting,
  update,
  reminderSchedule,
  updateReminderSchedule,
  loadScheduledReminderCount,
  customReminderMessageFormCount,
  loading,
}: MessageContainerProps) => {
  const {
    leadPro,
    matterFlow,
    "LINK-3736": automaticRemindersFlag,
  } = useFeatureFlags();

  return (
    <div className="h-full">
      <div className="border-b border-neutral-100 p-[1.6rem]">
        Create message templates for sending intake forms to clients.
      </div>
      <div className="h-full overflow-y-auto">
        {leadPro && (
          <Accordion label="New Intake Message (Lead)">
            <MessageSection
              description="This will be the default message when a new client intake form is sent for a lead."
              loading={loading}
              value={appendFormLink(
                setting?.lead?.trim() || defaultTemplate.lead,
              )}
              fallback={defaultTemplate.lead}
              save={(lead) => update({ lead })}
              excludingFieldNames={["downloadFormLink"]}
            />
          </Accordion>
        )}
        {matterFlow && (
          <Accordion label="New Intake Message (Matter)">
            <MessageSection
              description="This will be the default message when a new client intake form is sent for a matter."
              loading={loading}
              value={appendFormLink(
                setting?.matter?.trim() || defaultTemplate.matter,
              )}
              fallback={defaultTemplate.matter}
              save={(matter) => update({ matter })}
              excludingFieldNames={["downloadFormLink"]}
            />
          </Accordion>
        )}
        <Accordion label="Reminder Message" expandedClassName="max-h-[120rem]">
          <div className="flex flex-col gap-large">
            <CustomReminderMessageBanner
              count={customReminderMessageFormCount}
            />
            {automaticRemindersFlag && (
              <ReminderScheduleSettings
                value={reminderSchedule}
                update={updateReminderSchedule}
                loadScheduledReminderCount={loadScheduledReminderCount}
                loading={loading}
              />
            )}
            <MessageSection
              description="This will be the default message when a reminder is sent for a form."
              loading={loading}
              value={appendFormLink(
                setting?.reminder?.trim() || defaultTemplate.reminder,
              )}
              fallback={defaultTemplate.reminder}
              save={(reminder) => update({ reminder })}
              excludingFieldNames={["downloadFormLink"]}
            />
          </div>
        </Accordion>
        <Accordion label="Confirmation Message">
          {loading && (
            <div className="p-[1.6rem]">
              <Spinner size="regular" color="primary" />
            </div>
          )}
          {!loading && (
            <ConfirmationMessage
              confirmation={setting?.confirmation}
              redirectUrl={setting?.redirectUrl}
              update={update}
            />
          )}
        </Accordion>
      </div>
    </div>
  );
};
